
import { computed, defineComponent, onBeforeMount, PropType, reactive, toRaw } from 'vue'

// components
import Modal from '@/common/components/UI/Modal/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'
import Checkbox from '@/common/components/UI/Checkbox/index.vue'

// validation
import useVuelidate from '@vuelidate/core'
import { required } from '@vuelidate/validators'

// types
import { CompanyT } from '@/core/types/companies.types'
import { MerchantT } from '@/core/types/merchants.types'

// classes
import { DataState } from '@/core/classes/DataState.class'

// realisations
import { companies } from '@/core/realisations/companies.realisation'

const MARKETPLACES = [
    { id: 1, name: 'Kaspi' },
    { id: 2, name: 'Halyk' }
]

export default defineComponent({
    components: {
        Modal,
        TheInput,
        TheSelect,
        Checkbox,
    },
    props: {
        loading: {
            type: Boolean,
            default: false,
        },
        isCreate: {
            type: Boolean,
            default: false,
        },
        isUpdate: {
            type: Boolean,
            default: false,
        },
        isDelete: {
            type: Boolean,
            default: false,
        },
        initialValue: {
            type: Object as PropType<MerchantT>,
            default: null,
        },
    },
    emits: ['create', 'update', 'delete', 'cancel'],
    setup(props, { emit }) {
        const state = reactive({
            token: null as string | null,
            marketplace_id: null as number | null,
            company_id: null as number | null,
            is_active: 1,
        })

        const data_state = reactive({
            companies: new DataState<CompanyT>({}),
        })

        const state_form$ = useVuelidate(
            {
                token: { required },
                marketplace_id: { required },
                company_id: { required },
            },
            state
        )

        const loadCompanies = async (page: number, search?: string) => {
            data_state.companies.pagination.current_page = page

            try {
                data_state.companies.is_loading = true
                const response = await companies.getList({
                    page,
                    search,
                    statusId: 2,
                    isExtended: 0,
                })
                data_state.companies.list = response.data

                data_state.companies.pagination.total_pages = response.meta.last_page

                data_state.companies.is_failed = false
                data_state.companies.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.companies.failure.catchError(error)

                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_failed = true
                }
            } finally {
                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_loading = false
                }
            }
        }

        const loadMoreCompanies = async (page: number) => {
            if (page > data_state.companies.pagination.total_pages) return
            data_state.companies.pagination.current_page = page

            try {
                data_state.companies.is_loading = true

                const response = await companies.getList({ page, statusId: 2, isExtended: 0 })
                data_state.companies.list = data_state.companies.list.concat(response.data)

                data_state.companies.pagination.total_pages = response.meta.last_page

                data_state.companies.is_failed = false
                data_state.companies.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.companies.failure.catchError(error)

                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_failed = true
                }
            } finally {
                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_loading = false
                }
            }
        }

        const searchCompanies = (search_query: string) => {
            // data_state.companies.pagination.current_page
            loadCompanies(1, search_query)
        }

        const unshiftFindedCompany = async (company: CompanyT) => {
            await loadCompanies(data_state.companies.pagination.current_page)
            data_state.companies.list.unshift(company)
        }

        const modalTitle = computed(() => {
            if (props.isCreate) return 'Добавление мерчанта'
            if (props.isUpdate) return 'Редактирование мерчанта'
            if (props.isDelete) return 'Удаление мерчанта'
            return 'modal title'
        })

        const modalConfirmButtonText = computed(() => {
            if (props.isCreate) return 'Добавить'
            if (props.isUpdate) return 'Редактировать'
            if (props.isDelete) return 'Удалить'
            return 'Подтвердить'
        })

        onBeforeMount(() => {
            loadCompanies(1)

            if (!props.initialValue) return

            state.token = props.initialValue.token
            state.marketplace_id = props.initialValue.marketplace_id
            state.company_id = props.initialValue.company_id
            state.is_active = props.initialValue.is_active
        })

        const emitManager = () => {
            if (!props.isDelete) {
                state_form$.value.$touch()
                if (state_form$.value.$invalid) return
            }

            if (props.isCreate) emit('create', toRaw(state))
            if (props.isUpdate) emit('update', { id: props.initialValue.id, payload: toRaw(state) })
            if (props.isDelete) emit('delete', props.initialValue.id)
        }

        const emitCancel = () => {
            emit('cancel')
        }

        return {
            state,
            data_state,
            state_form$,
            modalTitle,
            modalConfirmButtonText,
            emitManager,
            emitCancel,
            loadCompanies,
            loadMoreCompanies,
            searchCompanies,
            unshiftFindedCompany,
            MARKETPLACES,
        }
    },
})
